import { ConvertUtils } from "@stellar/utils";

/** The units the user can give the limits of a custom size range in */
export const SIZE_UNITS = ["MB", "GB", "TB"];

/** A unit the user can give the limits of a custom size range in */

/** The unit a limit of a custom size range starts with */
export const DEFAULT_SIZE_UNIT = "GB";

/** The bytes in each unit, binary like the sizes shown in the projects list */
const SIZE_UNIT_BYTES = {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- a gigabyte has 1024 megabytes
  MB: ConvertUtils.GIGA_BYTE_SIZE / 1024,
  GB: ConvertUtils.GIGA_BYTE_SIZE,
  TB: ConvertUtils.TERA_BYTE_SIZE
};

/** Prefix of the ID of a custom size range, e.g. "range:500MB_2GB", "range:_2GB" or "range:1TB_" */
const SIZE_RANGE_FILTER_ID_PREFIX = "range:";

/** Separates the minimum and the maximum in the ID of a custom size range */
const SIZE_RANGE_SEPARATOR = "_";

/** A limit of a custom size range, as the user typed it */

/** The limits of a custom size range, of which at least one is set. Both are included */

/** A limit, e.g. "500MB", in the ID of a custom size range */
const SIZE_LIMIT_PATTERN = /^(\d+(?:\.\d+)?)([A-Z]+)$/;

/**
 * Returns the filter options for the sizes the projects can have. A preset includes its smaller
 * size and excludes its larger one, so every project is in exactly one of them. Only 1 TB itself
 * belongs to the range below it, as the last preset is "More than 1 TB".
 */
export function getProjectsSizeFilterPresets() {
  /* eslint-disable @typescript-eslint/no-magic-numbers -- the limits of the presets */
  // Sizes are whole bytes, so the next byte excludes 1 TB itself
  const moreThan1TB = toBytes(1, "TB") + 1;
  const presets = {
    lessThan1GB: {
      label: "Less than 1 GB",
      from: 1,
      to: toBytes(1, "GB")
    },
    from1GBTo10GB: {
      label: "1 GB - 10 GB",
      from: toBytes(1, "GB"),
      to: toBytes(10, "GB")
    },
    from10GBTo100GB: {
      label: "10 GB - 100 GB",
      from: toBytes(10, "GB"),
      to: toBytes(100, "GB")
    },
    from100GBTo1TB: {
      label: "100 GB - 1 TB",
      from: toBytes(100, "GB"),
      to: moreThan1TB
    },
    moreThan1TB: {
      label: "More than 1 TB",
      from: moreThan1TB,
      to: Infinity
    }
  };
  /* eslint-enable @typescript-eslint/no-magic-numbers */

  return {
    empty: {
      id: "empty",
      label: "Empty",
      hasEntityPassedFilter: project => getProjectSizeInBytes(project) === 0
    },
    ...Object.fromEntries(Object.entries(presets).map(([id, {
      label,
      from,
      to
    }]) => [id, {
      id,
      label,
      hasEntityPassedFilter: project => {
        const size = getProjectSizeInBytes(project);
        return size >= from && size < to;
      }
    }]))
  };
}

/**
 * Returns the filter options for the custom size ranges among the IDs, which match the projects
 * whose size is within both limits. The IDs that are no custom size range are left out.
 */
export function getCustomSizeFilterOptions(filterIds) {
  return Object.fromEntries(filterIds.flatMap(filterId => {
    const range = parseSizeRangeFilterId(filterId);
    if (!range) {
      return [];
    }
    const min = range.min ? toBytes(range.min.value, range.min.unit) : 0;
    const max = range.max ? toBytes(range.max.value, range.max.unit) : Infinity;
    return [[filterId, {
      id: filterId,
      label: formatSizeRange(range),
      hasEntityPassedFilter: project => {
        const size = getProjectSizeInBytes(project);
        return size >= min && size <= max;
      }
    }]];
  }));
}

/** Returns the ID of the filter for the custom size range */
export function getSizeRangeFilterId({
  min,
  max
}) {
  return `${SIZE_RANGE_FILTER_ID_PREFIX}${formatSizeLimitForId(min)}${SIZE_RANGE_SEPARATOR}${formatSizeLimitForId(max)}`;
}

/**
 * Returns the custom size range of the ID, or undefined if it is none or invalid: it has no
 * limit, or its minimum is larger than its maximum
 */
export function parseSizeRangeFilterId(filterId) {
  if (!filterId.startsWith(SIZE_RANGE_FILTER_ID_PREFIX)) {
    return undefined;
  }
  const limits = filterId.slice(SIZE_RANGE_FILTER_ID_PREFIX.length).split(SIZE_RANGE_SEPARATOR);
  if (limits.length !== 2) {
    return undefined;
  }
  const [min, max] = limits.map(parseSizeLimit);
  if (min === null || max === null || !min && !max) {
    return undefined;
  }
  if (min && max && toBytes(min.value, min.unit) > toBytes(max.value, max.unit)) {
    return undefined;
  }
  return {
    min,
    max
  };
}

/** Returns the text that tells which sizes a custom range matches, e.g. "500 MB - 2 GB" */
export function formatSizeRange({
  min,
  max
}) {
  if (min && max) {
    return `${formatSizeLimit(min)} - ${formatSizeLimit(max)}`;
  }
  return min ? `At least ${formatSizeLimit(min)}` : `Up to ${formatSizeLimit(max)}`;
}

/** Returns the size of the project in bytes, which is 0 when it has none */
function getProjectSizeInBytes(project) {
  return project.storage?.size ?? 0;
}

/** Returns the bytes of the amount of the unit */
function toBytes(value, unit) {
  return value * SIZE_UNIT_BYTES[unit];
}

/** Returns the limit as it is shown to the user, e.g. "500 MB" */
function formatSizeLimit(limit) {
  return limit ? `${limit.value} ${limit.unit}` : "";
}

/** Returns the limit as it is written in a filter ID, e.g. "500MB", or "" for no limit */
function formatSizeLimitForId(limit) {
  return limit ? `${limit.value}${limit.unit}` : "";
}

/**
 * Returns the limit written in a filter ID, undefined if there is none, or null if it is invalid
 */
function parseSizeLimit(text) {
  if (text === "") {
    return undefined;
  }
  const [, value, unit] = SIZE_LIMIT_PATTERN.exec(text) ?? [];
  if (value === undefined || !isSizeUnit(unit)) {
    return null;
  }
  return {
    value: Number(value),
    unit
  };
}

/** Whether the text is one of the units of a custom size range */
export function isSizeUnit(text) {
  return SIZE_UNITS.some(unit => unit === text);
}